import { useState } from 'react'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { useAdminUser, usePermission } from '../../hooks/usePermission'
import {
  memberships,
  money,
  MembershipRequestError,
  type Membership,
  type MembershipDetail,
  type RefundRequest,
  type SupportAction,
} from '../../services/memberships'

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : 'Request failed')
const date = (value: string | null) => (value ? new Date(value).toLocaleString() : 'None')
const card =
  'rounded-2xl bg-white dark:bg-[#1a1425] shadow-sm border border-gray-100 dark:border-[#2d2236]'
const selectClass =
  'rounded-lg border border-gray-200 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1425] px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7f4f9a]'
const alertClass =
  'rounded-xl bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-600 dark:text-red-400'
const sectionTitle = 'font-heading text-lg font-semibold text-gray-900 dark:text-white'
const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  succeeded: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  requires_action: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  past_due: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  canceled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
}

function Badge({ value }: { value: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[value] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}
    >
      {value.replaceAll('_', ' ')}
    </span>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-sm text-gray-900 dark:text-gray-100 break-all">{children}</dd>
    </div>
  )
}

type TimelineEvent = Awaited<ReturnType<typeof memberships.timeline>>[number]

const EVENT_TITLES: Record<string, string> = {
  webhook: 'Payment provider webhook',
  state: 'Membership state changed',
  cancel_scheduled: 'Cancellation scheduled',
  renewal_failed: 'Renewal payment failed',
  recovered: 'Payment recovered',
  ended: 'Membership ended',
  involuntary_end: 'Ended for non-payment',
  membership_support_action_requested: 'Support action requested',
  refund_membership_requested: 'Refund requested',
}
const humanize = (key: string) => {
  const text = key.replace(/_cents$/, '').replaceAll(/[_.]/g, ' ')
  return (text.charAt(0).toUpperCase() + text.slice(1)).replace(/\bid$/i, 'ID')
}
const eventTitle = (event: TimelineEvent) => EVENT_TITLES[event.kind] ?? humanize(event.kind)

function formatValue(key: string, value: unknown, currency: string): React.ReactNode {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number')
    return key.endsWith('_cents') ? money(value, currency) : value.toLocaleString()
  if (typeof value === 'string') {
    if (/(_at|_until|_end|_start)$/.test(key) && !Number.isNaN(Date.parse(value)))
      return date(value)
    if (/status$/.test(key)) return <Badge value={value} />
    if (key === 'event_type' || key === 'action') return humanize(value)
    if (/(^id|_id|^actor)$/.test(key)) return <span className="font-mono text-xs">{value}</span>
    return value
  }
  if (Array.isArray(value))
    return value.length ? value.map((item) => formatValue('', item, currency)).join(', ') : '—'
  return null
}

const eventSummary = (event: TimelineEvent, currency: string) => {
  const d = event.details
  const parts = [
    typeof d.event_type === 'string' && humanize(d.event_type),
    typeof d.action === 'string' && humanize(d.action),
    typeof d.amount_cents === 'number' && money(d.amount_cents, currency),
    typeof d.refunded_cents === 'number' && `${money(d.refunded_cents, currency)} refunded`,
    typeof d.status === 'string' && humanize(d.status),
    typeof d.reason === 'string' && d.reason,
  ].filter(Boolean)
  return parts.join(' · ')
}

function DetailFields({ data, currency }: { data: Record<string, unknown>; currency: string }) {
  const entries = Object.entries(data)
  const flat = entries.filter(([, v]) => !v || typeof v !== 'object' || Array.isArray(v))
  const nested = entries.filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v))
  return (
    <div className="space-y-4">
      {flat.length > 0 && (
        <dl className="grid grid-cols-1 gap-4 rounded-xl bg-gray-50 dark:bg-[#120e1b] p-4 sm:grid-cols-2">
          {flat.map(([key, value]) => (
            <Field key={key} label={humanize(key)}>
              {formatValue(key, value, currency)}
            </Field>
          ))}
        </dl>
      )}
      {nested.map(([key, value]) => (
        <div key={key} className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {humanize(key)}
          </h4>
          <DetailFields data={value as Record<string, unknown>} currency={currency} />
        </div>
      ))}
    </div>
  )
}

function Modal({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string
  subtitle?: React.ReactNode
  onClose?: () => void
  children: React.ReactNode
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-16 overflow-y-auto"
    >
      <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-[#1a1425] shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-[#3d2c4f] px-6 py-4">
          <div>
            <h2 className={sectionTitle}>{title}</h2>
            {subtitle && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-all">
                {subtitle}
              </p>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-[#2d2236]"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="px-6 py-5 space-y-4">{children}</div>
      </div>
    </div>
  )
}

const uuid = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}'

export function Memberships() {
  const canRead = usePermission('shops', 'read')
  const [scope, setScope] = useState<'membership' | 'shop_id' | 'buyer_user_id'>('membership')
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<Membership[]>([])
  const [selected, setSelected] = useState('')
  const [lookup, setLookup] = useState<{ scope: 'shop_id' | 'buyer_user_id'; id: string } | null>(
    null
  )
  const [more, setMore] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const search = async (append = false) => {
    setError('')
    setBusy(true)
    try {
      if (!append && scope === 'membership') {
        setSelected(query.trim())
        setLookup(null)
        setRows([])
        setMore(false)
        return
      }
      const target = append
        ? lookup
        : { scope: scope as 'shop_id' | 'buyer_user_id', id: query.trim() }
      if (!target) return
      const next = await memberships.search(target.scope, target.id, append ? rows.length : 0)
      setLookup(target)
      setRows(append ? [...rows, ...next] : next)
      setMore(next.length === 50)
      if (!append) setSelected('')
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setBusy(false)
    }
  }
  const [operations, setOperations] = useState<Record<string, number> | null>(null)
  const [operationsError, setOperationsError] = useState('')
  if (!canRead)
    return (
      <p role="alert" className={alertClass}>
        You do not have permission to view memberships.
      </p>
    )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-gray-900 dark:text-white">
          Creator memberships
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          Look up a membership, or find memberships by shop or buyer ID. Billing inspection and
          support changes are audited.
        </p>
      </div>
      <section className={`${card} p-4 space-y-3`}>
        <CustomButton
          variant="secondary"
          size="sm"
          onClick={() => {
            setOperationsError('')
            void memberships
              .operations()
              .then(setOperations)
              .catch(() => setOperationsError('Could not load membership operations.'))
          }}
        >
          Check membership operations
        </CustomButton>
        {operationsError && (
          <p role="alert" className={alertClass}>
            {operationsError}
          </p>
        )}
        {operations && (
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Object.entries(operations).map(([key, value]) => (
              <div key={key} className="rounded-xl bg-gray-50 dark:bg-[#120e1b] p-3">
                <dt className="text-xs capitalize text-gray-500 dark:text-gray-400">
                  {key.replaceAll('_', ' ')}
                </dt>
                <dd className="text-xl font-semibold text-gray-900 dark:text-white">{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          void search()
        }}
      >
        <label className="flex flex-col gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
          Search by
          <select
            aria-label="Search by"
            className={`${selectClass} py-3 font-normal`}
            value={scope}
            onChange={(e) => setScope(e.target.value as typeof scope)}
          >
            <option value="membership">Membership ID</option>
            <option value="shop_id">Shop ID</option>
            <option value="buyer_user_id">Buyer ID</option>
          </select>
        </label>
        <CustomTextInput
          label="ID"
          required
          pattern={uuid}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          className="w-80 max-w-full font-mono text-sm"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <CustomButton type="submit" loading={busy}>
          Search
        </CustomButton>
      </form>
      {error && (
        <p role="alert" className={alertClass}>
          {error}
        </p>
      )}
      {lookup && !rows.length && !busy && (
        <div className={`${card} py-10 text-center text-sm text-gray-400`}>
          No memberships found.
        </div>
      )}
      {rows.length > 0 && (
        <ul className={`${card} divide-y divide-gray-50 dark:divide-[#1e1630] overflow-hidden`}>
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => setSelected(row.id)}
                className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-[#2d2236] ${selected === row.id ? 'bg-gray-50 dark:bg-[#2d2236]' : ''}`}
              >
                <span className="font-mono text-xs text-gray-700 dark:text-gray-200 break-all">
                  {row.id}
                </span>
                <span className="flex shrink-0 items-center gap-3">
                  <Badge value={row.status} />
                  <span className="text-gray-900 dark:text-gray-100">
                    {money(row.amount_cents, row.currency)}/month
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {more && (
        <CustomButton variant="secondary" disabled={busy} onClick={() => void search(true)}>
          More memberships
        </CustomButton>
      )}
      {selected && <MembershipPanel key={selected} id={selected} />}
    </div>
  )
}

function MembershipPanel({ id }: { id: string }) {
  const canEdit = usePermission('shops', 'edit')
  const { userId } = useAdminUser()
  const [timeline, setTimeline] = useState<TimelineEvent[]>([])
  const [timelineOffset, setTimelineOffset] = useState(0)
  const [timelineError, setTimelineError] = useState('')
  const [openEvent, setOpenEvent] = useState<TimelineEvent | null>(null)
  const loadTimeline = async (page: number) => {
    try {
      setTimeline(await memberships.timeline(id, page))
      setTimelineOffset(page)
      setTimelineError('')
    } catch {
      setTimelineError('Could not load event history.')
    }
  }
  const [detail, setDetail] = useState<MembershipDetail | null>(null)
  const [offset, setOffset] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [action, setAction] = useState<SupportAction>('reconcile')
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [refund, setRefund] = useState<RefundRequest | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [refundConfirmed, setRefundConfirmed] = useState(false)
  const currency = detail?.membership.currency ?? 'usd'
  const load = async (page = 0) => {
    setBusy(true)
    setError('')
    try {
      setDetail(await memberships.detail(id, page))
      setOffset(page)
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setBusy(false)
    }
  }
  const execute = async () => {
    setBusy(true)
    setError('')
    setMessage('')
    try {
      await memberships.action(id, action, reason.trim())
      setMessage('Support action completed. Restoring access does not restart renewal.')
      setConfirmed(false)
      setDetail(await memberships.detail(id, offset))
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setBusy(false)
    }
  }
  const sendRefund = async () => {
    if (!refund) return
    setBusy(true)
    setSubmitted(true)
    setError('')
    setMessage('')
    try {
      const result = await memberships.refund(id, refund)
      setRefund(null)
      setSubmitted(false)
      setRefundConfirmed(false)
      setMessage(`Refund ${result.status}.`)
      setDetail(await memberships.detail(id, offset))
    } catch (cause) {
      setError(errorMessage(cause))
      if (cause instanceof MembershipRequestError && cause.definitive) {
        // Rejected outright: the request ID is spent, so reopen the draft under a new one.
        setRefund({ ...refund, request_id: crypto.randomUUID() })
        setSubmitted(false)
        setRefundConfirmed(false)
        void memberships.detail(id, offset).then(setDetail, () => {})
      }
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className={`${card} p-6 space-y-6`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className={sectionTitle}>Membership</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5 break-all">
            {id}
          </p>
        </div>
        <CustomButton size="sm" disabled={busy} onClick={() => void load(offset)}>
          {detail ? 'Refresh billing records' : 'Load billing records'}
        </CustomButton>
      </div>
      {error && !refund && (
        <p role="alert" className={alertClass}>
          {error}
        </p>
      )}
      {message && (
        <p
          role="status"
          className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300"
        >
          {message}
        </p>
      )}
      <section className="space-y-3" aria-label="Membership event history">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className={sectionTitle}>Billing, webhook and admin history</h3>
          <CustomButton variant="secondary" size="sm" onClick={() => void loadTimeline(0)}>
            Load event history
          </CustomButton>
        </div>
        {timelineError && (
          <p role="alert" className={alertClass}>
            {timelineError}
          </p>
        )}
        {timeline.length > 0 && (
          <ol className="divide-y divide-gray-100 dark:divide-[#2d2236] rounded-xl border border-gray-100 dark:border-[#2d2236] overflow-hidden">
            {timeline.map((event) => (
              <li key={`${event.source}:${event.id}`}>
                <button
                  type="button"
                  onClick={() => setOpenEvent(event)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-[#2d2236]"
                >
                  <Badge value={event.source} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                      {eventTitle(event)}
                    </span>
                    <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                      {eventSummary(event, currency) || 'No additional details'}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    {date(event.created_at)}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        )}
        <div className="flex gap-3">
          {timelineOffset > 0 && (
            <CustomButton
              variant="secondary"
              size="sm"
              onClick={() => void loadTimeline(timelineOffset - 50)}
            >
              Newer events
            </CustomButton>
          )}
          {timeline.length === 50 && timelineOffset < 450 && (
            <CustomButton
              variant="secondary"
              size="sm"
              onClick={() => void loadTimeline(timelineOffset + 50)}
            >
              Older events
            </CustomButton>
          )}
        </div>
      </section>
      {detail && (
        <>
          <dl className="grid grid-cols-2 gap-4 rounded-xl bg-gray-50 dark:bg-[#120e1b] p-4 sm:grid-cols-3">
            <Field label="Shop">{detail.membership.shop_id}</Field>
            <Field label="Buyer">{detail.membership.buyer_user_id}</Field>
            <Field label="Status">
              <span className="flex flex-wrap gap-1.5">
                <Badge value={detail.membership.status} />
                {detail.membership.suspended && <Badge value="Access suspended" />}
              </span>
            </Field>
            <Field label="Renewal">
              {detail.membership.cancel_at_period_end ? 'Canceled' : 'Not scheduled to cancel'}
            </Field>
            <Field label="Paid through">{date(detail.membership.paid_until)}</Field>
            <Field label="Grace through">{date(detail.membership.grace_until)}</Field>
          </dl>
          {canEdit && (
            <form
              className="space-y-3 border-t border-gray-100 dark:border-[#2d2236] pt-6"
              onSubmit={(e) => {
                e.preventDefault()
                void execute()
              }}
            >
              <h3 className={sectionTitle}>Support action</h3>
              <label className="flex flex-col gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                Action
                <select
                  aria-label="Support action"
                  value={action}
                  onChange={(e) => {
                    setAction(e.target.value as SupportAction)
                    setConfirmed(false)
                  }}
                  className={`${selectClass} font-normal sm:w-96`}
                >
                  <option value="reconcile">Reconcile with payment provider</option>
                  <option value="suspend">Suspend access and stop renewal</option>
                  <option value="restore">Restore eligible paid access</option>
                </select>
              </label>
              <CustomTextInput
                label="Support reason"
                required
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded accent-[#7f4f9a]"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                I confirm this action for the membership above.
              </label>
              <CustomButton type="submit" disabled={busy || !confirmed || !reason.trim()}>
                Apply support action
              </CustomButton>
            </form>
          )}
          <h3 className={sectionTitle}>Invoices</h3>
          {!detail.invoices.length && (
            <p className="text-sm text-gray-400">No invoices on this page.</p>
          )}
          {detail.invoices.map((invoice) => (
            <article
              key={invoice.id}
              className="rounded-xl border border-gray-100 dark:border-[#2d2236] p-4 space-y-2 text-sm text-gray-700 dark:text-gray-200"
            >
              <p className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs break-all">{invoice.id}</span>
                <Badge value={invoice.status} />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {date(invoice.created_at)}
                </span>
              </p>
              <p>
                Paid {money(invoice.amount_paid_cents, invoice.currency)} · Refunded{' '}
                {money(invoice.refunded_cents, invoice.currency)}
              </p>
              {invoice.dispute_status && <p>Dispute: {invoice.dispute_status}</p>}
              {invoice.hosted_invoice_url && (
                <a
                  className="text-xs font-medium text-[#7f4f9a] hover:text-[#503864] dark:text-[#b58fcc]"
                  href={invoice.hosted_invoice_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Invoice receipt
                </a>
              )}
              {invoice.pending_refund && (
                <p className="text-amber-700 dark:text-amber-300">
                  A refund is pending. Review its request below before retrying.
                </p>
              )}
              {canEdit &&
                !invoice.pending_refund &&
                invoice.status === 'paid' &&
                invoice.refundable_cents > 0 &&
                (!invoice.dispute_status || invoice.dispute_status === 'won') && (
                  <CustomButton
                    variant="secondary"
                    size="sm"
                    disabled={busy || !!refund}
                    onClick={() => {
                      setRefund({
                        request_id: crypto.randomUUID(),
                        shop_id: detail.membership.shop_id,
                        invoice_id: invoice.id,
                        amount_cents: invoice.refundable_cents,
                        reason: '',
                      })
                      setSubmitted(false)
                      setRefundConfirmed(false)
                    }}
                  >
                    Prepare refund
                  </CustomButton>
                )}
            </article>
          ))}
          {canEdit && refund && (
            <Modal
              title={submitted ? 'Retry refund' : 'Issue refund'}
              subtitle={<span className="font-mono">{refund.invoice_id}</span>}
              onClose={submitted ? undefined : () => setRefund(null)}
            >
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault()
                  void sendRefund()
                }}
              >
                {error && (
                  <p role="alert" className={alertClass}>
                    {error}
                  </p>
                )}
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Amounts are in currency minor units (100 cents = $1 USD). Full current-period
                  refunds end access and renewal. Retry an uncertain result with this same request.
                </p>
                <CustomTextInput
                  label="Refund amount in minor units"
                  type="number"
                  min={1}
                  max={
                    submitted
                      ? undefined
                      : detail.invoices.find((invoice) => invoice.id === refund.invoice_id)
                          ?.refundable_cents
                  }
                  step={1}
                  required
                  disabled={submitted}
                  value={refund.amount_cents}
                  onChange={(e) => setRefund({ ...refund, amount_cents: Number(e.target.value) })}
                />
                <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Refunds {money(refund.amount_cents || 0, currency)} to the buyer.
                </p>
                <CustomTextInput
                  label="Refund reason"
                  maxLength={500}
                  required
                  disabled={submitted}
                  value={refund.reason}
                  onChange={(e) => setRefund({ ...refund, reason: e.target.value })}
                />
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded accent-[#7f4f9a]"
                    checked={refundConfirmed}
                    onChange={(e) => setRefundConfirmed(e.target.checked)}
                  />
                  I confirm this refund amount and invoice.
                </label>
                <div className="flex gap-3 pt-1">
                  <CustomButton
                    type="submit"
                    variant="danger"
                    disabled={busy || !refundConfirmed || !refund.reason.trim()}
                  >
                    {submitted ? 'Retry same refund request' : 'Issue refund'}
                  </CustomButton>
                  {!submitted && (
                    <CustomButton type="button" variant="secondary" onClick={() => setRefund(null)}>
                      Discard draft
                    </CustomButton>
                  )}
                </div>
              </form>
            </Modal>
          )}
          <h3 className={sectionTitle}>Refund request history</h3>
          {!detail.refund_attempts.length && (
            <p className="text-sm text-gray-400">No refund requests on this page.</p>
          )}
          {detail.refund_attempts.map((attempt) => (
            <article
              key={attempt.request_id}
              className="rounded-xl border border-gray-100 dark:border-[#2d2236] p-4 space-y-2 break-all text-sm text-gray-700 dark:text-gray-200"
            >
              <p className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs">{attempt.request_id}</span>
                <Badge value={attempt.status} />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {date(attempt.created_at)}
                </span>
              </p>
              <p>
                Invoice: {attempt.invoice_id} · Amount: {attempt.amount_cents} minor units
              </p>
              <p>
                Actor: {attempt.actor_user_id} ({attempt.actor_role}) · Reason: {attempt.reason}
              </p>
              {canEdit &&
                attempt.actor_user_id === userId &&
                attempt.actor_role === 'support' &&
                ['pending', 'requires_action'].includes(attempt.status) && (
                  <CustomButton
                    size="sm"
                    disabled={busy || !!refund}
                    onClick={() => {
                      setRefund({
                        request_id: attempt.request_id,
                        shop_id: detail.membership.shop_id,
                        invoice_id: attempt.invoice_id,
                        amount_cents: attempt.amount_cents,
                        reason: attempt.reason,
                      })
                      setSubmitted(true)
                      setRefundConfirmed(false)
                    }}
                  >
                    Recover this refund request
                  </CustomButton>
                )}
            </article>
          ))}
          <div className="flex items-center justify-between">
            <CustomButton
              variant="secondary"
              disabled={busy || offset === 0 || !!refund}
              onClick={() => void load(Math.max(0, offset - 20))}
            >
              Previous billing page
            </CustomButton>
            <CustomButton
              variant="secondary"
              disabled={
                busy ||
                !!refund ||
                (detail.invoices.length < 20 && detail.refund_attempts.length < 20)
              }
              onClick={() => void load(offset + 20)}
            >
              Next billing page
            </CustomButton>
          </div>
        </>
      )}
      {openEvent && (
        <Modal
          title={eventTitle(openEvent)}
          subtitle={date(openEvent.created_at)}
          onClose={() => setOpenEvent(null)}
        >
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Badge value={openEvent.source} />
            <span className="font-mono break-all">{openEvent.id}</span>
          </div>
          {Object.keys(openEvent.details).length ? (
            <DetailFields data={openEvent.details} currency={currency} />
          ) : (
            <p className="text-sm text-gray-400">No additional details were recorded.</p>
          )}
        </Modal>
      )}
    </section>
  )
}
